// SupportView — agency side of platform support.
//
// Two ways in, shown at the top:
//   1. "Asistente IA" — a first-line assistant grounded on the Deskte manual
//      (Azure OpenAI, BAA-covered). It answers "how do I…" questions. When it
//      can't help it offers to escalate, pre-filling a ticket with the chat.
//   2. "Crear ticket" — go straight to a ticket if you'd rather a person.
//
// Tickets are a thread with the Deskte team; message bodies are encrypted at
// rest on the backend, and image attachments (screenshots) are stored as
// encrypted blobs. Replies arrive live via the App-level SSE handler,
// re-broadcast as `deskte:support-message` / `deskte:support-ticket` window
// events. Dates render en-US (feedback_date_format_always_en_us).

import { useEffect, useRef, useState } from 'react';
import { LifeBuoy, Send, Loader2, Plus, ChevronLeft, CheckCircle, Clock, AlertCircle, Sparkles, Paperclip, X, Image as ImageIcon } from 'lucide-react';
import { BlobImg, BlobLink } from './BlobMedia.jsx';
import {
  listSupportTickets,
  createSupportTicket,
  getSupportTicket,
  sendSupportMessage,
  askSupportAI,
} from '../api.js';

const CATEGORIES = [
  { key: 'question', es: 'Pregunta', en: 'Question' },
  { key: 'bug', es: 'Problema / Error', en: 'Bug / Problem' },
  { key: 'billing', es: 'Facturación', en: 'Billing' },
  { key: 'suggestion', es: 'Sugerencia', en: 'Suggestion' },
  { key: 'other', es: 'Otro', en: 'Other' },
];
function catLabel(key, es) {
  const c = CATEGORIES.find((x) => x.key === key);
  return c ? (es ? c.es : c.en) : key;
}
function statusMeta(status, es) {
  switch (status) {
    case 'resolved': return { label: es ? 'Resuelto' : 'Resolved', color: '#19c37d', Icon: CheckCircle };
    case 'in_progress': return { label: es ? 'En proceso' : 'In progress', color: '#f4c430', Icon: Clock };
    default: return { label: es ? 'Abierto' : 'Open', color: '#00d4ff', Icon: AlertCircle };
  }
}
function fmtWhen(at) {
  try { return new Date(at).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
}

// Burbuja de MIS mensajes: fondo suave del acento y la letra normal del tema.
// Antes era el acento lleno con letra azul oscuro fija, que en el tema claro
// (acento azul oscuro) no se leia (Lissette 2026-10-22). Lo mismo que el Chat
// de Equipo (TeamChatView.jsx); la respuesta del administrador usa estas dos.
export const BURBUJA_MIA_FONDO = 'color-mix(in srgb, var(--c-cyan,#00d4ff) 16%, var(--c-surface,#0d1c2e))';
export const BURBUJA_MIA_BORDE = '1px solid color-mix(in srgb, var(--c-cyan,#00d4ff) 45%, transparent)';

// Read image File objects → data URLs (≤5 MB each, images only).
function filesToDataUrls(files, cb) {
  const imgs = Array.from(files || []).filter((f) => f.type.startsWith('image/') && f.size <= 5_000_000);
  imgs.forEach((f) => {
    const reader = new FileReader();
    reader.onload = () => cb(reader.result);
    reader.readAsDataURL(f);
  });
}

// Pegar una captura con Ctrl+V / Cmd+V DONDE SE ESCRIBE, no solo encima del
// recuadro de adjuntar (Lissette 2026-10-22: "que la pueda pegar"). Devuelve el
// manejador para el onPaste del cuadro de texto: si lo pegado trae imagenes,
// las agrega como adjuntos (maximo 6); si es texto, no se toca y se pega normal.
export function pegarImagenes(setItems) {
  return (e) => {
    const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    e.preventDefault();
    filesToDataUrls(files, (url) => setItems((prev) => (prev.length >= 6 ? prev : [...prev, url])));
  };
}

// Reusable attachment picker: button + drag-drop + paste. Holds data URLs in
// the parent's `items` state. Tambien lo usa la respuesta del administrador
// (PlatformAdminView), para que los dos lados adjunten igual.
export function ImageAttach({ items, setItems, lang }) {
  const es = lang === 'es';
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const add = (url) => setItems((prev) => (prev.length >= 6 ? prev : [...prev, url]));
  const onPaste = pegarImagenes(setItems);
  return (
    <div
      onPaste={onPaste}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(false); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(false); filesToDataUrls(e.dataTransfer.files, add); }}
      style={{ border: `1px dashed ${drag ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'}`, borderRadius: 8, padding: '.5rem .6rem', background: drag ? 'rgba(0,212,255,.06)' : 'transparent' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => inputRef.current?.click()} style={{ ...btnGhost, height: 30, fontSize: '.74rem', padding: '0 10px' }}>
          <Paperclip size={13} /> {es ? 'Adjuntar imagen' : 'Attach image'}
        </button>
        <span style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)' }}>
          {es ? 'o arrastra / pega una captura (Ctrl+V también en el texto)' : 'or drag / paste a screenshot (Ctrl+V works in the text too)'}
        </span>
        <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={(e) => { filesToDataUrls(e.target.files, add); e.target.value = ''; }} />
      </div>
      {items.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {items.map((url, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={url} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--c-border,#1a3550)' }} />
              <button type="button" onClick={() => setItems((prev) => prev.filter((_, j) => j !== i))}
                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', border: 'none', background: '#ff5d8f', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Render a message's image attachments (already signed for read).
function AttachThumbs({ attachments }) {
  if (!Array.isArray(attachments) || !attachments.length) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
      {attachments.map((a, i) => (
        <BlobLink key={i} href={a.url} target="_blank" rel="noreferrer">
          <BlobImg src={a.url} alt="" style={{ maxWidth: 160, maxHeight: 160, borderRadius: 8, border: '1px solid var(--c-border,#1a3550)', display: 'block' }} />
        </BlobLink>
      ))}
    </div>
  );
}

export default function SupportView({ currentUser, lang = 'en', entry = null, onEntryConsumed, aiName = 'Deskte AI' }) {
  const es = lang === 'es';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState(null); // null | 'ai' | 'new'
  const [prefill, setPrefill] = useState(null); // { body?, category? } from AI escalation or the 🎧 menu

  // React to the top-bar 🎧 menu / Help Center choice:
  //   'live'       → AI chat directly (the live assistant)
  //   'suggestion' → new feature-request ticket
  //   'ticket'     → new support ticket form, directly
  //   'help'       → support home / landing (my tickets + options)
  // Cleared after use.
  useEffect(() => {
    if (!entry) return;
    setSelectedId(null);
    if (entry === 'suggestion') { setPrefill({ category: 'suggestion' }); setMode('new'); }
    else if (entry === 'ticket') { setPrefill(null); setMode('new'); } // straight to the new-ticket form
    else if (entry === 'help') { setMode(null); } // landing / overview
    else { setMode('ai'); } // 'live' → AI chat directly
    onEntryConsumed?.();
  }, [entry]);

  const refreshList = () => {
    listSupportTickets()
      .then((rows) => { setTickets(rows); setLoading(false); })
      .catch((err) => { setError(err.message || 'Failed to load'); setLoading(false); });
  };
  useEffect(() => { refreshList(); }, []);
  useEffect(() => {
    const onAny = () => refreshList();
    window.addEventListener('deskte:support-message', onAny);
    window.addEventListener('deskte:support-ticket', onAny);
    return () => {
      window.removeEventListener('deskte:support-message', onAny);
      window.removeEventListener('deskte:support-ticket', onAny);
    };
  }, []);

  const goNewTicket = (pf) => { setPrefill(pf || null); setMode('new'); };

  if (mode === 'ai') {
    return <AskAI lang={lang} aiName={aiName} onClose={() => setMode(null)} onEscalate={(pf) => goNewTicket(pf)} />;
  }
  if (mode === 'new') {
    return <NewTicket lang={lang} prefill={prefill} onCancel={() => { setMode(null); setPrefill(null); }} onCreated={(t) => { setMode(null); setPrefill(null); refreshList(); setSelectedId(t.id); }} />;
  }
  if (selectedId) {
    return <TicketThread id={selectedId} currentUser={currentUser} lang={lang} onBack={() => { setSelectedId(null); refreshList(); }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--c-surface2,#112538)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LifeBuoy size={20} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--c-white,#eef6ff)', fontWeight: 700 }}>{es ? 'Soporte' : 'Support'}</h2>
          <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Pregúntale a la IA o abre un ticket al equipo de Deskte' : 'Ask the AI or open a ticket with the Deskte team'}</div>
        </div>
      </div>

      {/* Top alternatives: AI first, ticket as the fallback */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
        <button onClick={() => setMode('ai')} style={bigCard}>
          <div style={{ ...bigIcon, background: 'rgba(124,92,255,.15)' }}><Sparkles size={20} style={{ color: '#a78bfa' }} /></div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ color: 'var(--c-white,#eef6ff)', fontWeight: 700, fontSize: '.9rem' }}>{es ? 'Pregúntale a la IA' : 'Ask the AI'}</div>
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Respuesta al instante sobre cómo usar Deskte' : 'Instant answers on how to use Deskte'}</div>
          </div>
        </button>
        <button onClick={() => goNewTicket(null)} style={bigCard}>
          <div style={{ ...bigIcon, background: 'rgba(0,212,255,.15)' }}><Plus size={20} style={{ color: 'var(--c-cyan,#00d4ff)' }} /></div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ color: 'var(--c-white,#eef6ff)', fontWeight: 700, fontSize: '.9rem' }}>{es ? 'Crear ticket' : 'Create ticket'}</div>
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Habla con una persona del equipo' : 'Talk to a person on the team'}</div>
          </div>
        </button>
      </div>

      <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7da2c8)', fontWeight: 600, marginBottom: 8 }}>{es ? 'Mis tickets' : 'My tickets'}</div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text3,#45688a)' }}><Loader2 size={16} className="dkt-spin" /> {es ? 'Cargando…' : 'Loading…'}</div>
      ) : error ? (
        <div style={{ color: '#ff6b6b', fontSize: '.8rem' }}>{error}</div>
      ) : tickets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 28, color: 'var(--c-text3,#45688a)', fontSize: '.82rem' }}>
          {es ? 'Aún no has abierto tickets.' : "You haven't opened any tickets yet."}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tickets.map((t) => {
            const sm = statusMeta(t.status, es);
            const unread = t.lastFromOwner && (!t.agencyReadAt || new Date(t.lastMessageAt) > new Date(t.agencyReadAt));
            return (
              <button key={t.id} onClick={() => setSelectedId(t.id)} style={{ ...rowCard, borderColor: unread ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  {unread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--c-cyan,#00d4ff)', flexShrink: 0 }} />}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--c-white,#eef6ff)', fontWeight: 600, fontSize: '.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.subject}</div>
                    <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 2 }}>{catLabel(t.category, es)} · {fmtWhen(t.lastMessageAt)}</div>
                  </div>
                </div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.7rem', fontWeight: 600, color: sm.color, flexShrink: 0 }}>
                  <sm.Icon size={13} /> {sm.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AskAI({ lang, onClose, onEscalate, aiName = 'Deskte AI' }) {
  const es = lang === 'es';
  const [messages, setMessages] = useState([]); // { role:'user'|'ai', text, needsHuman? }
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [escalate, setEscalate] = useState(false);
  const listRef = useRef(null);
  // Auto-scroll the message box (NOT the whole page) to the latest message.
  // scrollIntoView would pull the window down on open; scrolling the inner
  // container's scrollTop keeps the page where it is.
  useEffect(() => { const el = listRef.current; if (el && messages.length) el.scrollTop = el.scrollHeight; }, [messages.length, busy]);

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    setText(''); setBusy(true);
    try {
      const { reply, needsHuman } = await askSupportAI({ message: q, history });
      setMessages((prev) => [...prev, { role: 'ai', text: reply, needsHuman }]);
      if (needsHuman) setEscalate(true);
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'ai', text: (es ? 'No pude responder. ' : 'I could not answer. ') + (err.message || ''), needsHuman: true }]);
      setEscalate(true);
    } finally { setBusy(false); }
  };

  // Build a ticket body pre-filled with the conversation.
  const escalateNow = () => {
    const transcript = messages.map((m) => `${m.role === 'user' ? (es ? 'Yo' : 'Me') : 'IA'}: ${m.text}`).join('\n');
    onEscalate({ body: (es ? 'Resumen de mi conversación con la IA:\n\n' : 'Summary of my conversation with the AI:\n\n') + transcript });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)', minHeight: 420 }}>
      <button onClick={onClose} style={backLink}><ChevronLeft size={15} /> {es ? 'Volver a Soporte' : 'Back to Support'}</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0 10px' }}>
        <Sparkles size={18} style={{ color: '#a78bfa' }} />
        <div>
          <div style={{ color: 'var(--c-white,#eef6ff)', fontWeight: 700, fontSize: '1rem' }}>{es ? `Asistente IA — ${aiName}` : `${aiName} Assistant`}</div>
          <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Te ayudo con cómo usar Deskte. Si no puedo, te abro un ticket.' : 'I help with how to use Deskte. If I can\'t, I\'ll open a ticket.'}</div>
        </div>
      </div>

      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.length === 0 && !busy && (
          <div style={{ margin: 'auto', textAlign: 'center', color: 'var(--c-text3,#45688a)', padding: 16 }}>
            <Sparkles size={26} style={{ opacity: 0.5, marginBottom: 8, color: '#a78bfa' }} />
            <div style={{ fontSize: '.85rem' }}>{es ? 'Escribe tu pregunta abajo.' : 'Type your question below.'}</div>
            <div style={{ fontSize: '.74rem', marginTop: 4 }}>{es ? 'Ej: "¿Cómo importo mis clientes?"' : 'e.g. "How do I import my clients?"'}</div>
          </div>
        )}
        {messages.map((m, i) => {
          const ai = m.role === 'ai';
          return (
            <div key={i} style={{ display: 'flex', flexDirection: ai ? 'row' : 'row-reverse' }}>
              <div style={{ maxWidth: '82%' }}>
                <div style={{ fontSize: '.66rem', color: ai ? '#a78bfa' : 'var(--c-text3,#45688a)', fontWeight: 700, marginBottom: 2, textAlign: ai ? 'left' : 'right' }}>{ai ? 'IA' : (es ? 'Tú' : 'You')}</div>
                <div style={{ background: ai ? 'var(--c-surface2,#112538)' : BURBUJA_MIA_FONDO, color: 'var(--c-white,#eef6ff)', border: ai ? '1px solid var(--c-border,#1a3550)' : BURBUJA_MIA_BORDE, borderRadius: 12, padding: '.55rem .75rem', fontSize: '.85rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
              </div>
            </div>
          );
        })}
        {busy && <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.78rem' }}><Loader2 size={13} className="dkt-spin" /> {es ? 'Pensando…' : 'Thinking…'}</div>}
      </div>

      {escalate && (
        <div style={{ marginTop: 10, padding: '.7rem .85rem', background: 'rgba(0,212,255,.08)', border: '1px solid rgba(0,212,255,.3)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '.8rem', color: 'var(--c-white,#eef6ff)' }}>{es ? '¿Esto necesita al equipo? Crea un ticket con la conversación.' : 'Need the team? Create a ticket with this conversation.'}</span>
          <button onClick={escalateNow} style={btnPrimary}><Plus size={14} /> {es ? 'Crear ticket' : 'Create ticket'}</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-end' }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder={es ? 'Escribe tu pregunta…' : 'Type your question…'} style={composerInput} />
        <button onClick={send} disabled={busy || !text.trim()} style={{ ...btnPrimary, height: 40, opacity: busy || !text.trim() ? 0.6 : 1 }}>
          {busy ? <Loader2 size={15} className="dkt-spin" /> : <Send size={15} />} {es ? 'Enviar' : 'Send'}
        </button>
      </div>
    </div>
  );
}

function NewTicket({ lang, prefill, onCancel, onCreated }) {
  const es = lang === 'es';
  const isSuggestion = prefill?.category === 'suggestion';
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState(prefill?.category || 'question');
  const [body, setBody] = useState(prefill?.body || '');
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!subject.trim() || !body.trim() || busy) return;
    setBusy(true); setError('');
    try {
      const t = await createSupportTicket({ subject: subject.trim(), category, body: body.trim(), attachments: images });
      onCreated(t);
    } catch (err) { setError(err.message || 'Failed to create'); setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 660 }}>
      <button onClick={onCancel} style={backLink}><ChevronLeft size={15} /> {es ? 'Volver' : 'Back'}</button>
      <h2 style={{ fontSize: '1.05rem', color: 'var(--c-white,#eef6ff)', fontWeight: 700, margin: '6px 0 14px' }}>
        {isSuggestion ? (es ? '💡 Sugerir una función' : '💡 Feature request') : (es ? 'Nuevo ticket de soporte' : 'New support ticket')}
      </h2>

      <label style={lbl}>{es ? 'Asunto' : 'Subject'}</label>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder={es ? 'Resume tu problema en una línea' : 'Summarize your issue in one line'} style={inp} />

      <label style={lbl}>{es ? 'Categoría' : 'Category'}</label>
      <select value={category} onChange={(e) => setCategory(e.target.value)} style={inp}>
        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{es ? c.es : c.en}</option>)}
      </select>

      <label style={lbl}>{es ? 'Descripción' : 'Description'}</label>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} onPaste={pegarImagenes(setImages)} rows={6} maxLength={5000} placeholder={es ? 'Cuéntanos qué pasó, qué esperabas y los pasos para reproducirlo.' : 'Tell us what happened, what you expected, and steps to reproduce.'} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />

      <label style={lbl}>{es ? 'Imágenes / capturas (opcional)' : 'Images / screenshots (optional)'}</label>
      <ImageAttach items={images} setItems={setImages} lang={lang} />

      <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', margin: '8px 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <AlertCircle size={12} /> {es ? 'No incluyas datos de asegurados (SSN, salud) aquí.' : 'Do not include insured-member data (SSN, health) here.'}
      </div>

      {error && <div style={{ color: '#ff6b6b', fontSize: '.78rem', marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={submit} disabled={busy || !subject.trim() || !body.trim()} style={{ ...btnPrimary, opacity: busy || !subject.trim() || !body.trim() ? 0.6 : 1 }}>
          {busy ? <Loader2 size={15} className="dkt-spin" /> : <Send size={15} />} {es ? 'Enviar' : 'Send'}
        </button>
        <button onClick={onCancel} style={btnGhost}>{es ? 'Cancelar' : 'Cancel'}</button>
      </div>
    </div>
  );
}

function TicketThread({ id, currentUser, lang, onBack }) {
  const es = lang === 'es';
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  const load = () => {
    getSupportTicket(id)
      .then((d) => { setTicket(d.ticket); setMessages(d.messages || []); setLoading(false); })
      .catch((err) => { setError(err.message || 'Failed to load'); setLoading(false); });
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    const onMsg = (e) => {
      const m = e?.detail;
      if (!m || m.ticketId !== id) return;
      setMessages((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
    };
    const onTicket = (e) => { if (e?.detail?.id === id) setTicket((t) => ({ ...t, ...e.detail })); };
    window.addEventListener('deskte:support-message', onMsg);
    window.addEventListener('deskte:support-ticket', onTicket);
    return () => {
      window.removeEventListener('deskte:support-message', onMsg);
      window.removeEventListener('deskte:support-ticket', onTicket);
    };
  }, [id]);
  // Scroll the message box (not the page) to the newest message.
  useEffect(() => { const el = listRef.current; if (el && messages.length) el.scrollTop = el.scrollHeight; }, [messages.length]);

  const send = async () => {
    const body = text.trim();
    if ((!body && !images.length) || sending) return;
    setSending(true); setError('');
    try {
      const saved = await sendSupportMessage(id, body || (es ? '(imagen)' : '(image)'), images);
      setMessages((prev) => prev.some((x) => x.id === saved.id) ? prev : [...prev, saved]);
      setText(''); setImages([]);
    } catch (err) {
      setError(err.message || 'Failed to send');
    } finally { setSending(false); }
  };

  const sm = ticket ? statusMeta(ticket.status, es) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)', minHeight: 420 }}>
      <button onClick={onBack} style={backLink}><ChevronLeft size={15} /> {es ? 'Volver a Soporte' : 'Back to Support'}</button>
      {ticket && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: '6px 0 10px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: 'var(--c-white,#eef6ff)', fontWeight: 700, fontSize: '1rem' }}>{ticket.subject}</div>
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>{catLabel(ticket.category, es)}</div>
          </div>
          {sm && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.74rem', fontWeight: 600, color: sm.color, border: `1px solid ${sm.color}55`, borderRadius: 999, padding: '.25rem .7rem' }}><sm.Icon size={13} /> {sm.label}</span>}
        </div>
      )}

      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading ? (
          <div style={{ margin: 'auto', color: 'var(--c-text3,#45688a)' }}><Loader2 size={16} className="dkt-spin" /> {es ? 'Cargando…' : 'Loading…'}</div>
        ) : messages.map((m) => {
          const fromTeam = m.fromOwner;
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: fromTeam ? 'row' : 'row-reverse' }}>
              <div style={{ maxWidth: '82%' }}>
                <div style={{ fontSize: '.66rem', color: fromTeam ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)', fontWeight: 700, marginBottom: 2, textAlign: fromTeam ? 'left' : 'right' }}>
                  {fromTeam ? (es ? 'Equipo Deskte' : 'Deskte Team') : (m.author?.seatLabel || m.author?.displayName || (es ? 'Tú' : 'You'))}
                </div>
                <div style={{ background: fromTeam ? 'var(--c-surface2,#112538)' : BURBUJA_MIA_FONDO, color: 'var(--c-white,#eef6ff)', border: fromTeam ? '1px solid var(--c-border,#1a3550)' : BURBUJA_MIA_BORDE, borderRadius: 12, padding: '.55rem .75rem', fontSize: '.85rem', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                <AttachThumbs attachments={m.attachments} />
                <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', marginTop: 2, textAlign: fromTeam ? 'left' : 'right' }}>{fmtWhen(m.createdAt)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {error && <div style={{ color: '#ff6b6b', fontSize: '.74rem', marginTop: 6 }}>{error}</div>}

      <div style={{ marginTop: 10 }}>
        <ImageAttach items={images} setItems={setImages} lang={lang} />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} onPaste={pegarImagenes(setImages)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder={es ? 'Escribe una respuesta, o pega una captura…' : 'Type a reply, or paste a screenshot…'} style={composerInput} />
          <button onClick={send} disabled={sending || (!text.trim() && !images.length)} style={{ ...btnPrimary, height: 40, opacity: sending || (!text.trim() && !images.length) ? 0.6 : 1 }}>
            {sending ? <Loader2 size={15} className="dkt-spin" /> : <Send size={15} />} {es ? 'Enviar' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

const btnPrimary = { flexShrink: 0, height: 38, padding: '0 14px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'var(--c-cyan,#00d4ff)', color: '#06283d', fontWeight: 700, fontSize: '.82rem', display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnGhost = { height: 38, padding: '0 14px', borderRadius: 10, border: '1px solid var(--c-border,#1a3550)', cursor: 'pointer', background: 'transparent', color: 'var(--c-text2,#7da2c8)', fontWeight: 600, fontSize: '.82rem', display: 'inline-flex', alignItems: 'center', gap: 6 };
const backLink = { background: 'none', border: 'none', color: 'var(--c-text2,#7da2c8)', cursor: 'pointer', fontSize: '.78rem', display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0 };
const rowCard = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', textAlign: 'left', background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.7rem .85rem', cursor: 'pointer' };
const bigCard = { display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1rem', cursor: 'pointer' };
const bigIcon = { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const lbl = { display: 'block', fontSize: '.72rem', color: 'var(--c-text2,#7da2c8)', fontWeight: 600, margin: '10px 0 4px' };
const inp = { width: '100%', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem .7rem', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' };
const composerInput = { flex: 1, resize: 'none', maxHeight: 120, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.6rem .75rem', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', outline: 'none', fontFamily: 'inherit', lineHeight: 1.4 };
